import { Link } from "react-router-dom";
import Footer from "@/components/landing/Footer";
import { Wordmark } from "@/components/brand/Wordmark";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="terms-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-6 flex items-baseline justify-between">
          <Link to="/" className="font-display text-xl tracking-tight">
            <Wordmark className="h-8" />
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Last updated: 2026-02-04
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Terms of service</p>
        <h1 className="font-display text-4xl sm:text-5xl">A short, fair contract.</h1>
        <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-prose">
          Working draft. Final terms will be reviewed by counsel before commercial launch.
        </p>

        <Section title="1. Service">
          <p>
            Wardrobe Index ("the Service") is a SaaS application for cataloguing and tracking
            wardrobe assets. We provide the Service "as is" with reasonable commercial effort.
          </p>
        </Section>

        <Section title="2. Account">
          <p>
            You're responsible for keeping your password secret and for the activity in your
            workspace. Tell us immediately at <a className="text-oxblood underline underline-offset-4" href="mailto:security@wardrobeindex.com">security@wardrobeindex.com</a> if you suspect a breach.
          </p>
        </Section>

        <Section title="3. Acceptable use">
          <ul className="list-disc pl-5 space-y-2">
            <li>Do not upload illegal content, malware, or material you don't have rights to.</li>
            <li>Do not attempt to bypass billing, rate limits, or other users' workspaces.</li>
            <li>Do not scrape or republish other users' shared lookbooks.</li>
          </ul>
          <p>We may suspend a workspace for material breach, with notice when feasible.</p>
        </Section>

        <Section title="4. Subscription &amp; payment">
          <ul className="list-disc pl-5 space-y-2">
            <li>Plans, prices, and trial length are listed at <Link to="/#pricing" className="text-oxblood underline underline-offset-4">/#pricing</Link>.</li>
            <li>You authorise [PAYMENT PROCESSOR: Stripe] to charge your card on the renewal date.</li>
            <li>Cancellations stop renewals; access continues until the end of the billing period.</li>
            <li>We don't pro-rate refunds for partial months. We will refund duplicate charges immediately.</li>
          </ul>
        </Section>

        <Section title="5. Your content">
          <p>
            You keep ownership of everything you upload. We get a limited licence to store, copy,
            and display it solely to operate the Service for you.
          </p>
        </Section>

        <Section title="6. Confidentiality">
          <p>
            We treat your workspace contents as confidential. Staff access is need-to-know, logged,
            and reviewed.
          </p>
        </Section>

        <Section title="7. Liability">
          <p>
            To the maximum extent permitted by law, our aggregate liability for any claim arising
            from the Service is capped at the fees you paid us in the 12 months preceding the claim.
            We aren't liable for indirect or consequential losses.
          </p>
        </Section>

        <Section title="8. Termination">
          <p>
            You can close your workspace any time. We may close inactive free-trial workspaces after
            [INACTIVE_DAYS: 90] days of no sign-in. We'll email you 14 days before doing so.
          </p>
        </Section>

        <Section title="9. Governing law">
          <p>
            These terms are governed by the laws of [JURISDICTION]. Disputes will be resolved in
            [VENUE].
          </p>
        </Section>

        <p className="mt-12 text-xs text-muted-foreground">[LAST UPDATED: 2026-02-04] · [ENTITY: Wardrobe Index Ltd.]</p>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl">{title}</h2>
      <div className="mt-3 text-sm leading-relaxed text-foreground/80 space-y-3 max-w-prose">
        {children}
      </div>
    </section>
  );
}
